// src/components/Visualization/PerformanceBar.js
import React from 'react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell, ReferenceLine, LabelList } from 'recharts';

/**
 * PerformanceBar - Gráfico de barras para comparação de performance
 * 
 * @param {Object} props
 * @param {Array} props.data - Dados para o gráfico
 * @param {string} props.dataKey - Chave para os valores
 * @param {string} props.nameKey - Chave para os nomes das barras
 * @param {string} props.sortBy - Ordenar por ('value', 'name', 'none')
 * @param {string} props.orientation - Orientação ('vertical', 'horizontal')
 * @param {boolean} props.showValues - Mostrar valores nas barras
 * @param {Function} props.valueFormatter - Formatar valores
 * @param {number} props.maxItems - Número máximo de itens a mostrar
 * @param {string} props.positiveColor - Cor para valores positivos
 * @param {string} props.negativeColor - Cor para valores negativos
 * @param {Object} props.threshold - Valor de referência para colorir barras
 * @param {boolean} props.showTarget - Mostrar linha de meta
 * @param {number} props.targetValue - Valor da meta
 * @param {number} props.height - Altura do gráfico
 */
const PerformanceBar = ({
  data = [],
  dataKey,
  nameKey = 'name',
  sortBy = 'value',
  orientation = 'horizontal',
  showValues = true,
  valueFormatter = value => value,
  maxItems = 10,
  positiveColor = '#4ade80',
  negativeColor = '#f87171',
  threshold,
  showTarget = false,
  targetValue,
  height = 300
}) => {
  // Processar e ordenar dados
  const processedData = React.useMemo(() => {
    if (!data || !dataKey) return [];
    
    // Filtrar dados inválidos
    let filteredData = data.filter(item => 
      item && 
      item[dataKey] !== undefined && 
      item[dataKey] !== null && 
      !isNaN(item[dataKey]) &&
      item[nameKey]
    );
    
    // Ordenar dados
    if (sortBy === 'value') {
      filteredData = [...filteredData].sort((a, b) => b[dataKey] - a[dataKey]);
    } else if (sortBy === 'name') {
      filteredData = [...filteredData].sort((a, b) => a[nameKey].localeCompare(b[nameKey]));
    }
    
    // Limitar número de itens
    if (maxItems > 0 && filteredData.length > maxItems) {
      filteredData = filteredData.slice(0, maxItems);
    }
    
    return filteredData;
  }, [data, dataKey, nameKey, sortBy, maxItems]);
  
  // Determinar a cor da barra com base no valor e threshold
  const getBarColor = (value) => {
    if (threshold !== undefined) {
      return value >= threshold ? positiveColor : negativeColor;
    }
    return value >= 0 ? positiveColor : negativeColor;
  };
  
  // Determinar layout com base na orientação
  const isHorizontal = orientation === 'horizontal';
  
  // Tooltip customizado
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="bg-white p-3 shadow-md border border-gray-200 rounded-md">
          <p className="font-medium text-sm">{data[nameKey]}</p>
          <p className="text-sm mt-1">{valueFormatter(data[dataKey])}</p>
        </div>
      );
    }
    return null;
  };
  
  // Se não tiver dados, mostrar mensagem
  if (!processedData.length) {
    return (
      <div className="flex items-center justify-center h-full bg-gray-50 rounded-lg border border-gray-200 p-4">
        <p className="text-gray-500 text-sm">Nenhum dado disponível para exibição.</p>
      </div>
    );
  }
  
  return (
    <div style={{ height, width: '100%' }}>
      <ResponsiveContainer width="100%" height="100%">
        <BarChart
          data={processedData}
          layout={isHorizontal ? 'vertical' : 'horizontal'}
          margin={{ top: 10, right: 30, left: 20, bottom: 10 }}
        >
          {/* Eixos */}
          {isHorizontal ? (
            <>
              <XAxis type="number" domain={['auto', 'auto']} />
              <YAxis 
                type="category" 
                dataKey={nameKey} 
                width={120}
                tick={{ fontSize: 12 }}
              />
            </>
          ) : (
            <>
              <XAxis 
                type="category" 
                dataKey={nameKey}
                tick={{ fontSize: 12, angle: -45, textAnchor: 'end' }}
                height={60}
              />
              <YAxis type="number" domain={['auto', 'auto']} />
            </>
          )}
          
          {/* Tooltip */}
          <Tooltip content={<CustomTooltip />} />
          
          {/* Linha de meta */}
          {showTarget && targetValue !== undefined && (
            <ReferenceLine
              {...(isHorizontal ? { x: targetValue } : { y: targetValue })}
              stroke="#6366f1"
              strokeDasharray="3 3"
              strokeWidth={2}
              label={{
                value: `Meta: ${valueFormatter(targetValue)}`,
                fill: '#4f46e5',
                fontSize: 12,
                position: isHorizontal ? 'right' : 'top'
              }}
            />
          )}
          
          {/* Barras */}
          <Bar dataKey={dataKey} fill={positiveColor} radius={[4, 4, 4, 4]}>
            {processedData.map((entry, index) => (
              <Cell key={`cell-${index}`} fill={getBarColor(entry[dataKey])} />
            ))}
            
            {/* Rótulos de valores */}
            {showValues && (
              <LabelList
                dataKey={dataKey}
                position={isHorizontal ? 'right' : 'top'}
                formatter={valueFormatter}
                style={{ fontSize: 11, fill: '#4b5563' }}
              />
            )}
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
};

export default PerformanceBar;
